import { rgba } from 'polished'
import styled, { css } from 'styled-components'
import { mb, mt } from 'styled-components-spacing'

import { colors } from 'bl-common/src/constants/colors'
import {
  base,
  gridOffsetFrom,
  gridOffsetTo,
} from 'bl-common/src/constants/sizes'
import { Link } from 'bl-common/src/elements/Link'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { between } from 'bl-common/src/utils/between'
import { media, mediaMax } from 'bl-common/src/utils/media'

type ContainerProps = {
  top?: any
  bottom?: any
}

const Container = styled.div<ContainerProps>`
  position: relative;
  ${({ top, theme }) => mt(top, theme)};

  ${({ top }) =>
    top?.xs &&
    mediaMax.md(css`
      margin-top: ${top.xs * base}px;
    `)};

  ${({ bottom, theme }) => mb(bottom, theme)};

  ${({ bottom }) =>
    bottom?.xs &&
    mediaMax.md(css`
      margin-bottom: ${bottom.xs * base}px;
    `)};

  ${mediaMax.md(css`
    margin-bottom: 100px;
    margin-top: 24px;

    &::after {
      background: linear-gradient(334.28deg, #dee9ef 0%, #f0f0ed 100%);
      content: '';
      height: 100%;
      left: 30vw;
      position: absolute;
      top: 0;
      width: 70vw;
      z-index: -1;
    }
  `)}
`

const Inner = styled.div`
  margin-left: ${gridOffsetFrom}px;
  padding: 48px 0 80px 0;

  ${media.md(css`
    background: linear-gradient(334.28deg, #dee9ef 0%, #f0f0ed 100%);
    padding: ${({ theme }) => `${theme.spacing[8]} ${theme.spacing[4.5]}`};
    display: flex;
    margin-left: ${between(gridOffsetFrom, gridOffsetTo)};
    min-height: 36rem;
  `)}
`

const Content = styled.div`
  ${media.md(css`
    max-width: ${({ theme }) => theme.spacing[30]};
    margin-right: ${({ theme }) => theme.spacing[10]};
    flex: 0 1 60%;
  `)}
`

const Heading = styled(props => <Type {...props} />)`
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  color: ${rgba(colors.midGrey, 0.5)};

  ${media.md(css`
    color: ${colors.midGrey};
    font-size: ${({ theme }) => theme.spacing[3]};
    text-transform: none;
  `)}
`

const Subtitle = styled(props => <Type {...props} />)`
  font-size: ${({ theme }) => theme.spacing[1.5]};
  ${mediaMax.md(css`
    display: none;
  `)}
`

const LinkType = styled(props => <Type {...props} />)`
  font-weight: 600;
  font-size: 24px;
  line-height: 42px;
`

const List = styled.ul`
  flex: 0 1 40%;
`

const ListItem = styled.li`
  ${media.md(css`
    margin-bottom: 8px;
    &:hover > * {
      color: ${colors.deepBlue};
    }
  `)}
`

export const ExploreTopics = ({ heading, description, topics = undefined }) => {
  if (!topics) {
    return null
  }
  return (
    <Container top={{ xs: 1.5, md: 4 }} bottom={{ xs: 1.5, md: 4 }}>
      <Inner>
        <Content>
          <Heading bottom={{ xs: 2, md: 1 }}>{heading}</Heading>
          <Subtitle>{description}</Subtitle>
        </Content>
        <List>
          {topics.map(topic => (
            <ListItem key={topic.fields.topicSlug}>
              <Link to={`/stories/topic/${topic.fields.topicSlug}`}>
                <LinkType preset="headlineSmall">
                  {topic.fields.topicName}
                </LinkType>
              </Link>
            </ListItem>
          ))}
        </List>
      </Inner>
    </Container>
  )
}
